/**
 * Responsive hooks for detecting screen sizes and application context
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isMobile as checkIsMobile,
  isTablet as checkIsTablet,
  isDesktop as checkIsDesktop
} from '../constants/responsive.constants';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(checkIsMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };

    window.addEventListener('resize', handleResize);

    // Check on mount in case of SSR
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

export function useIsTablet(): boolean {
  const [isTablet, setIsTablet] = useState(checkIsTablet);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(checkIsTablet());
    };

    window.addEventListener('resize', handleResize);

    // Check on mount in case of SSR
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isTablet;
}

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(checkIsDesktop);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(checkIsDesktop());
    };

    window.addEventListener('resize', handleResize);

    // Check on mount in case of SSR
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isDesktop;
}

// Hook for detecting PowerEditor context
export function useIsPowerEditor(): boolean {
  const location = useLocation();
  const [isPowerEditor, setIsPowerEditor] = useState(false);

  useEffect(() => {
    // Check if current URL matches PowerEditor pattern
    // Pattern: /dashboard/editor/{projectId}/power
    const powerEditorPattern = /^\/dashboard\/editor\/[^\/]+\/power(?:\/.*)?$/;
    const isInPowerEditor = powerEditorPattern.test(location.pathname);

    setIsPowerEditor(isInPowerEditor);
  }, [location.pathname]);

  return isPowerEditor;
}

// Hook for detecting mobile PowerEditor context (combines both conditions)
export function useIsMobilePowerEditor(): boolean {
  const isMobile = useIsMobile();
  const isPowerEditor = useIsPowerEditor();

  return isMobile && isPowerEditor;
}

// Hook for detecting tablet PowerEditor context
export function useIsTabletPowerEditor(): boolean {
  const isTablet = useIsTablet();
  const isPowerEditor = useIsPowerEditor();

  return isTablet && isPowerEditor;
}

// Hook for detecting desktop PowerEditor context
export function useIsDesktopPowerEditor(): boolean {
  const isDesktop = useIsDesktop();
  const isPowerEditor = useIsPowerEditor();

  return isDesktop && isPowerEditor;
}

// Combined hook for getting all responsive states
export function useResponsive() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const isPowerEditor = useIsPowerEditor();
  const isMobilePowerEditor = useIsMobilePowerEditor();
  const isTabletPowerEditor = useIsTabletPowerEditor();
  const isDesktopPowerEditor = useIsDesktopPowerEditor();

  return {
    isMobile,
    isTablet,
    isDesktop,
    isPowerEditor,
    isMobilePowerEditor,
    isTabletPowerEditor,
    isDesktopPowerEditor,
  } as const;
}